import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { getResizedImageUrl } from '../lib/utils';

export default function References() {
  const [galleries, setGalleries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeImage, setActiveImage] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('alle');

  useEffect(() => {
    // Fetch galleries from CMS
    fetch('https://api.dev.auxo.dk/public/content/galleri')
      .then(res => {
        if (!res.ok) throw new Error('Kunne ikke hente gallerier');
        return res.json();
      })
      .then(data => {
        setGalleries(data || []);
        setLoading(false);
      })
      .catch(err => {
        console.error('Fejl ved hentning af galleri:', err);
        setLoading(false);
      });
  }, []);

  // Extract all images with metadata
  const allProjects = galleries.flatMap(g => 
    (g.images || []).map((imgUrl, idx) => ({
      id: `${g.id}-${idx}`,
      url: imgUrl,
      title: g.title?.da || 'Udført arbejde',
      categorySlug: g.gallery_slug || 'andet',
      categoryName: g.title?.da || 'Andet',
      description: g.description?.da || ''
    }))
  );

  // Get unique categories for filtering
  const categories = [
    { slug: 'alle', name: 'Alle projekter' },
    ...galleries.map(g => ({
      slug: g.gallery_slug,
      name: g.title?.da
    }))
  ];

  // Filter projects based on selected category
  const filteredProjects = selectedCategory === 'alle'
    ? allProjects
    : allProjects.filter(p => p.categorySlug === selectedCategory);

  return (
    <div className="w-full">
      {/* Page Banner */}
      <section className="relative h-[40vh] min-h-[250px] w-full overflow-hidden bg-primary-green">
        <img 
          src="https://assets.auxodev.dk/cdn-cgi/image/width=1600,quality=80,format=auto/assets/340cd821-77c4-4f44-a38b-9945cfcac15b/hero-danish-garden.jpg" 
          alt="Referencer" 
          className="absolute inset-0 h-full w-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-primary-green via-primary-green/50 to-transparent" />
        {/* Decorative Background Leaves */}
        <div className="absolute inset-0 pointer-events-none opacity-10 flex justify-between items-center px-12 overflow-hidden z-10">
          <i className="fa-solid fa-leaf text-white text-5xl animate-float-leaf-1 transform translate-y-12" />
          <i className="fa-solid fa-leaf text-white text-4xl animate-float-leaf-2 transform -translate-y-12 hidden md:block" />
          <i className="fa-solid fa-leaf text-white text-6xl animate-float-leaf-3 transform translate-y-6 hidden lg:block" />
        </div>
        <div className="relative z-20 mx-auto flex h-full max-w-7xl flex-col justify-end px-4 pb-12 sm:px-6 lg:px-8 text-white">
          <span className="text-xs font-bold uppercase tracking-widest text-secondary-green block mb-2">
            Vores portefølje
          </span>
          <h1 className="font-display text-3xl font-bold tracking-tight text-white sm:text-4xl lg:text-5xl uppercase">
            Referencer
          </h1>
        </div>
      </section>

      {/* Main Content & Gallery */}
      <section className="py-16 md:py-24 bg-white relative overflow-hidden">
        {/* Decorative Background Leaves */}
        <div className="absolute inset-0 pointer-events-none opacity-5 flex justify-between items-center px-12 overflow-hidden">
          <i className="fa-solid fa-leaf text-primary-green text-5xl animate-float-leaf-1 transform translate-y-12" />
          <i className="fa-solid fa-leaf text-primary-green text-4xl animate-float-leaf-2 transform -translate-y-12 hidden md:block" />
          <i className="fa-solid fa-leaf text-primary-green text-6xl animate-float-leaf-3 transform translate-y-6 hidden lg:block" />
        </div>
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center max-w-3xl mx-auto mb-12 space-y-4">
            <span className="text-xs font-bold uppercase tracking-widest text-secondary-green block">
              Udført arbejde
            </span>
            <h2 className="font-display text-2xl md:text-3xl font-bold text-primary-green">
              Vores grønne resultater
            </h2>
            <p className="font-body text-sm text-neutral-muted leading-relaxed">
              Her kan du gå på opdagelse i vores udførte arbejde. Brug filteret herunder til at sortere projekterne efter de forskellige ydelser. Klik på et billede for at se det i stor visning.
            </p>
          </div>

          {loading ? (
            <div className="flex justify-center py-16">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-secondary-green" />
            </div>
          ) : (
            <div className="space-y-12">
              {/* Category Filter Buttons */}
              <div className="flex flex-wrap justify-center gap-2.5">
                {categories.map((cat) => (
                  <button
                    key={cat.slug}
                    onClick={() => setSelectedCategory(cat.slug)}
                    className={`font-display text-[11px] font-bold uppercase tracking-wider px-5 py-2.5 rounded-sm transition-all duration-200 cursor-pointer ${
                      selectedCategory === cat.slug
                        ? 'bg-secondary-green text-white shadow-sm'
                        : 'bg-neutral-light-bg text-neutral-muted hover:bg-gray-200'
                    }`}
                  >
                    {cat.name}
                  </button>
                ))}
              </div>

              {/* Responsive Gallery Grid */}
              {filteredProjects.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                  {filteredProjects.map((project) => (
                    <div 
                      key={project.id}
                      onClick={() => setActiveImage(project.url)}
                      className="group relative h-72 overflow-hidden rounded-sm bg-gray-100 shadow-xs cursor-pointer transition-all duration-300 hover:shadow-md"
                    >
                      <img 
                        src={getResizedImageUrl(project.url, 'width=600,height=450,fit=cover,quality=80,format=auto')} 
                        alt={project.title} 
                        className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                      {/* Hover Overlay */}
                      <div className="absolute inset-0 bg-gradient-to-t from-primary-green/90 via-primary-green/30 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6 text-white">
                        <span className="text-[10px] font-bold uppercase tracking-widest text-secondary-green bg-white px-2 py-1 rounded-xs self-start mb-2">
                          {project.categoryName}
                        </span>
                        <h3 className="font-display text-sm font-bold uppercase tracking-wider">
                          {project.title}
                        </h3>
                        {project.description && (
                          <p className="font-body text-[11px] text-gray-200 mt-1 leading-relaxed line-clamp-2">
                            {project.description}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-16 text-xs text-neutral-muted">
                  Ingen projekter fundet i denne kategori.
                </div>
              )}
            </div>
          )}
        </div>
      </section>

      {/* Lightbox Modal */}
      {activeImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 animate-in fade-in duration-200"
          onClick={() => setActiveImage(null)}
        >
          <button 
            className="absolute top-4 right-4 text-white text-3xl cursor-pointer hover:text-secondary-green transition-colors"
            onClick={() => setActiveImage(null)}
          >
            <i className="fa-solid fa-xmark" />
          </button>
          <img 
            src={activeImage} 
            alt="Udført arbejde i stor visning" 
            className="max-h-[90vh] max-w-full object-contain rounded-sm shadow-2xl"
          />
        </div>
      )}
    </div>
  );
}
